import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import { getCategories, createCategory, updateCategory, deleteCategory } from '../api/categoryApi';

const CategorySection = styled.div`
  background: #fff;
  border-radius: 24px;
  padding: 32px;
  box-shadow: 0 25px 60px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const CategoryHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

const CategoryIntro = styled.div`
  max-width: 520px;
  color: #475467;
`;

const CategoryTable = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  overflow: hidden;
`;

const TableHead = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  background: #f8fafc;
  padding: 14px 20px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
`;

const TableBody = styled.div``;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  padding: 18px 20px;
  align-items: center;
  border-top: 1px solid #eef2ff;
  background: #ffffff;

  &:nth-child(even) {
    background: #fdfdfd;
  }
`;

const TableCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
`;

const ColorSwatch = styled.span`
  width: 36px;
  height: 36px;
  border-radius: 12px;
  background: ${(props) => props.$color || '#0f172a'};
  border: 1px solid rgba(15, 23, 42, 0.12);
  flex-shrink: 0;
`;

const NameText = styled.span`
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CategoryColorValue = styled.span`
  font-size: 13px;
  color: #475467;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const TypeBadge = styled.span`
  align-self: flex-start;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: ${(props) => (props.$type === 'Income' ? 'rgba(22, 163, 74, 0.08)' : 'rgba(220, 38, 38, 0.08)')};
  color: ${(props) => (props.$type === 'Income' ? '#15803d' : '#b91c1c')};
`;

const ActionsCell = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  font-size: 12px;
  color: #0f172a;
  cursor: pointer;

  &:hover {
    background: #f8fafc;
  }
`;

const DangerButton = styled.button`
  padding: 6px 12px;
  border-radius: 999px;
  border: none;
  background: #fee2e2;
  color: #b91c1c;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: #fecaca;
  }
`;

const EmptyPlaceholder = styled.div`
  padding: 32px;
  border: 1px dashed #cbd5f5;
  border-radius: 18px;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
`;

const EmptyRow = styled.div`
  padding: 24px;
  text-align: center;
  color: #94a3b8;
`;

const AddButton = styled.button`
  padding: 10px 16px;
  border-radius: 999px;
  border: none;
  background: #111322;
  color: #f9fafb;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.2);

  &:hover {
    background: #020617;
  }
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const SearchInput = styled.input`
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  padding: 9px 16px 9px 36px;
  font-size: 14px;
  min-width: 220px;
  background: #fff url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="%2394a3b8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"%3E%3Ccircle cx="11" cy="11" r="8"/%3E%3Cpath d="m21 21-4.35-4.35"/%3E%3C/svg%3E') no-repeat 12px center;

  &:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 1px rgba(99, 102, 241, 0.2);
  }
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 40;
`;

const ModalCard = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 24px 24px 20px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 25px 60px rgba(15, 23, 42, 0.25);
`;

const ModalTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 18px;
  color: #0f172a;
`;

const ModalSubtitle = styled.p`
  margin: 0 0 18px;
  font-size: 13px;
  color: #64748b;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
`;

const Label = styled.label`
  font-size: 13px;
  color: #0f172a;
  font-weight: 500;
`;

const Input = styled.input`
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  padding: 8px 10px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 1px rgba(99, 102, 241, 0.2);
  }
`;

const ModalActions = styled.div`
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const SecondaryButton = styled.button`
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #0f172a;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    background: #f8fafc;
  }
`;

const PrimaryButton = styled.button`
  padding: 8px 14px;
  border-radius: 999px;
  border: none;
  background: #111322;
  color: #f9fafb;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  opacity: ${(props) => (props.disabled ? 0.7 : 1)};

  &:hover {
    background: #020617;
  }
`;

const ErrorText = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: #dc2626;
`;

const CategoryPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState('#2563eb');
  const [type, setType] = useState('Income');
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchCategories = async () => {
    try {
      setError(null);
      const response = await getCategories();
      const data = response?.data?.data || [];
      setCategories(data);
    } catch (err) {
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      await fetchCategories();
    };

    load();

    return () => {
      isMounted = false;
    };
  }, []);

  const resetForm = () => {
    setName('');
    setColor('#2563eb');
    setType('Income');
    setFormError(null);
  };

  const handleOpenModal = () => {
    resetForm();
    setEditingCategory(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (category) => {
    setName(category.name || '');
    setColor(category.color || '#2563eb');
    setType(category.type || 'Income');
    setFormError(null);
    setEditingCategory(category);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (saving) return;
    setIsModalOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !color.trim() || !type) {
      setFormError('Name, color, and type are required');
      return;
    }

    setFormError(null);
    setSaving(true);
    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, {
          name: name.trim(),
          color: color.trim(),
          type
        });
      } else {
        await createCategory({ name: name.trim(), color: color.trim(), type });
      }
      await fetchCategories();
      setIsModalOpen(false);
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to create category';
      setFormError(message);
    } finally {
      setSaving(false);
    }
  };

  const filteredCategories = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return categories;
    }

    return categories.filter((category) => {
      const nameMatch = category.name?.toLowerCase().includes(term);
      const typeMatch = category.type?.toLowerCase().includes(term);
      const colorMatch = category.color?.toLowerCase().includes(term);
      return nameMatch || typeMatch || colorMatch;
    });
  }, [categories, searchTerm]);

  const handleDelete = async (category) => {
    const result = await Swal.fire({
      title: 'Delete category?',
      text: `This will remove "${category.name}" permanently.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#b91c1c',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    try {
      await deleteCategory(category.id);
      await fetchCategories();
      await Swal.fire({
        title: 'Deleted',
        text: 'Category was removed.',
        icon: 'success',
        timer: 1200,
        showConfirmButton: false
      });
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to delete category';
      Swal.fire({
        title: 'Error',
        text: message,
        icon: 'error'
      });
    }
  };

  return (
    <CategorySection>
      <CategoryHeader>
        <div>
          <h2 style={{ margin: 0, color: '#0f172a' }}>Strategy categories</h2>
          <CategoryIntro>
            Centralized list of investable categories and their palette.
          </CategoryIntro>
        </div>
        <ControlsRow>
          <SearchInput
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, type, or color"
          />
          <AddButton type="button" onClick={handleOpenModal}>
            + Add category
          </AddButton>
        </ControlsRow>
      </CategoryHeader>

      {loading && <p>Loading categories...</p>}
      {error && !loading && <p style={{ color: '#dc2626' }}>{error}</p>}

      {!loading && !error && (
        <>
          {categories.length > 0 ? (
            <CategoryTable>
              <TableHead>
                <span>Name</span>
                <span>Type</span>
                <span>Color</span>
                <span style={{ textAlign: 'right' }}>Actions</span>
              </TableHead>
              <TableBody>
                {filteredCategories.length > 0 ? (
                  filteredCategories.map((category) => (
                    <TableRow key={category.id || category.name}>
                      <TableCell>
                        <NameCell>
                          <ColorSwatch $color={category.color} aria-hidden="true" />
                          <NameText title={category.name}>{category.name}</NameText>
                        </NameCell>
                      </TableCell>
                      <TableCell>
                        <TypeBadge $type={category.type}>{category.type}</TypeBadge>
                      </TableCell>
                      <TableCell>
                        <CategoryColorValue>{category.color}</CategoryColorValue>
                      </TableCell>
                      <TableCell>
                        <ActionsCell>
                          <ActionButton type="button" onClick={() => handleOpenEditModal(category)}>
                            Edit
                          </ActionButton>
                          <DangerButton type="button" onClick={() => handleDelete(category)}>
                            Delete
                          </DangerButton>
                        </ActionsCell>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <EmptyRow>No categories match your search.</EmptyRow>
                )}
              </TableBody>
            </CategoryTable>
          ) : (
            <EmptyPlaceholder>No categories yet. Add your first strategy bucket.</EmptyPlaceholder>
          )}
        </>
      )}

      {isModalOpen && (
        <ModalBackdrop onClick={handleCloseModal}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{editingCategory ? 'Edit category' : 'Add category'}</ModalTitle>
            <ModalSubtitle>
              Define a label, type, and color for this strategy bucket.
            </ModalSubtitle>

            <form onSubmit={handleSubmit}>
              <FormField>
                <Label htmlFor="category-name">Name</Label>
                <Input
                  id="category-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Growth Equity"
                />
              </FormField>

              <FormField>
                <Label htmlFor="category-color">Color</Label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Input
                    id="category-color"
                    type="text"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    placeholder="#2563eb"
                    style={{ flex: 1 }}
                  />
                  <Input
                    type="color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    style={{ width: 42, padding: 0 }}
                  />
                </div>
              </FormField>

              <FormField>
                <Label htmlFor="category-type">Type</Label>
                <select
                  id="category-type"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  style={{
                    borderRadius: 10,
                    border: '1px solid #e2e8f0',
                    padding: '8px 10px',
                    fontSize: 14,
                    outline: 'none'
                  }}
                >
                  <option value="Income">Income</option>
                  <option value="Expenses">Expenses</option>
                </select>
              </FormField>

              {formError && <ErrorText>{formError}</ErrorText>}

              <ModalActions>
                <SecondaryButton type="button" onClick={handleCloseModal} disabled={saving}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton type="submit" disabled={saving}>
                  {saving ? 'Saving…' : editingCategory ? 'Update category' : 'Save category'}
                </PrimaryButton>
              </ModalActions>
            </form>
          </ModalCard>
        </ModalBackdrop>
      )}
    </CategorySection>
  );
};

export default CategoryPage;
